import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import { HealthChartActions, HealthChartContext } from '@contexts/index';

import { Interval, INTERVALS, MetricVisual } from '@models/graph';

import { ReferenceRangeRiskLevel } from 'generated/types';

export function useHealthChart() {
  const context = React.useContext(HealthChartContext);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (context === undefined) {
    throw new Error(`useHealthChart must be used within a HealthChartProvider`);
  }

  const {
    state,
    dispatch,
    loading,
    data,
    category,
    code,
    referenceRangeMap,
    referenceRange,
  } = context;

  const normalRange = referenceRangeMap.get(ReferenceRangeRiskLevel.normal);

  const setDateFilters = (props: {
    interval?: Interval;
    minDate?: Date;
    maxDate?: Date;
    range?: number;
  }) => {
    dispatch({ type: HealthChartActions.SetFilters, ...props });
  };

  const setInterval = (props: { interval: Interval }) => {
    dispatch({ type: HealthChartActions.SetInterval, ...props });
  };

  const setSourceAndClinicalFilters = (props: {
    sources?: string[];
    clinicalStatus?: boolean;
  }) => {
    dispatch({ type: HealthChartActions.SetFilters, ...props });
  };

  const setView = (checked: boolean) => {
    const view = checked ? MetricVisual.Table : MetricVisual.Graph;
    dispatch({ type: HealthChartActions.SetView, ...{ view } });
  };

  const handleDateChange = async (
    intervalNum?: number,
    min?: Date,
    max?: Date,
    range?: number
  ) => {
    if (!min || !max) {
      return;
    }
    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    if (intervalNum === INTERVALS.Hour) {
      // If interval is 'hour', keep the datetime as is
      dateFrom = dayjs(min).toISOString();
      dateTo = dayjs(max).toISOString();
    } else {
      dateFrom = dayjs(min).startOf('day').toISOString();
      dateTo = dayjs(max).endOf('day').toISOString();
    }

    setDateFilters({
      maxDate: new Date(dateTo),
      minDate: new Date(dateFrom),
      range,
      interval:
        intervalNum === INTERVALS.Day
          ? Interval.Day
          : intervalNum === INTERVALS.Week
            ? Interval.Week
            : intervalNum === INTERVALS.Month
              ? Interval.Month
              : intervalNum === INTERVALS.Year
                ? Interval.Year
                : intervalNum === INTERVALS.Hour
                  ? Interval.Hour
                  : Interval.Custom,
    });
  };

  const filteredData = useMemo(() => {
    if (!state.sources?.length && typeof state.clinicalStatus !== 'boolean') {
      return data;
    }

    const filtered = data.filter((d) => {
      const matchesSource = state.sources?.length
        ? state.sources.includes(d.source)
        : true;
      const matchesClinicalStatus =
        typeof state.clinicalStatus === 'boolean'
          ? state.clinicalStatus
            ? d.clinicalNonClinical === 'Clinical'
            : d.clinicalNonClinical === 'Non-Clinical'
          : true;
      return matchesSource && matchesClinicalStatus;
    });
    return filtered;
  }, [data, state.clinicalStatus, state.sources]);

  return {
    ...state,
    referenceRangeMap,
    referenceRange,
    category,
    code,
    loading: loading || !state.isInitialized,
    setDateFilters,
    setSourceAndClinicalFilters,
    setInterval,
    data: filteredData,
    completeData: data,
    normalRange,
    setView,
    handleDateChange,
  };
}
